import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import ServiceCard from './ServiceCard';

const Services: React.FC = () => {
  const prefersReducedMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(!prefersReducedMotion);
  const [cardsToShow, setCardsToShow] = useState(3);

  // All 7 Services - per services_detail.md
  const services = [
    {
      title: 'Search Engine Optimisation',
      result: 'Appear at the top of Google when customers are searching',
      description:
        'Your ideal customers are searching for what you offer, but finding your competitors instead. We make sure your business shows up exactly when potential customers are looking for your services.',
      outcomes: [
        'Page one rankings for valuable search terms',
        'More qualified enquiries and leads',
        'Reduced reliance on paid advertising',
        'Long-term, sustainable growth',
      ],
      link: '/services/seo',
    },
    {
      title: 'Website Development',
      result: 'A website that converts visitors into customers',
      description:
        'Your website is your 24/7 digital sales team. If it is outdated, slow, or difficult to update, you are losing customers to competitors. We build high-performing websites that work exactly how your business needs them to.',
      outcomes: [
        'Faster loading times and better user experience',
        'More visitors converting into enquiries',
        'Easy content updates without technical knowledge',
        'Mobile-friendly on all devices',
      ],
      link: '/services/website-development',
    },
    {
      title: 'AI & Automation Solutions',
      result: 'Save time and scale your business with AI-powered tools',
      description:
        'Your team spends hours on repetitive tasks that could be automated. Customer enquiries go unanswered outside business hours. We integrate AI solutions that work for your business—from 24/7 virtual agents to workflow automation.',
      outcomes: [
        'Eliminate manual, repetitive tasks',
        '24/7 customer service without hiring staff',
        'Faster response times to enquiries',
        'Free up your team for strategic work',
      ],
      link: '/services/ai-automation',
    },
    {
      title: 'HubSpot Services',
      result: 'Maximise the potential of HubSpot with expert implementation',
      description:
        'You\'ve invested in HubSpot, but you\'re only scratching the surface of what it can do. We ensure your CRM and marketing tools are set up for success, tailored to your business needs.',
      outcomes: [
        'All customer data in one place',
        'Automated follow-ups that never miss a lead',
        'Clear visibility of your sales pipeline',
        'Marketing campaigns that run on autopilot',
      ],
      link: '/services/hubspot',
    },
    {
      title: 'Custom Apps',
      result: 'Reach your clients with a custom application',
      description:
        'Your customers expect a mobile experience, but off-the-shelf apps don\'t fit your business model. We build custom applications designed specifically for your business and your customers.',
      outcomes: [
        'A unique tool that differentiates you from competitors',
        'Better customer engagement through mobile access',
        'New revenue streams from app-based services',
        'Improved efficiency from custom internal tools',
      ],
      link: '/services/custom-apps',
    },
    {
      title: 'Pay Per Click Advertising',
      result: 'Generate qualified leads through targeted advertising',
      description:
        'You\'re spending money on advertising but not seeing the results. We create and manage advertising campaigns that attract qualified leads who are ready to buy.',
      outcomes: [
        'Lower cost per acquisition',
        'Higher quality leads that actually convert',
        'Clear ROI on advertising spend',
        'Scalable campaigns that grow with your business',
      ],
      link: '/services/ppc',
    },
    {
      title: 'Content Creation',
      result: 'Engage your community through tailored content campaigns',
      description:
        'You know content is important, but creating it consistently feels overwhelming. We create compelling content that resonates with your audience, from strategy to creation to distribution.',
      outcomes: [
        'Consistent content published without overwhelming your team',
        'Growing social media following and engagement',
        'More website traffic from content',
        'Position as an industry expert or thought leader',
      ],
      link: '/services/content-marketing',
    },
  ];

  // Detect screen size and set cards to show (debounced)
  useEffect(() => {
    const handleResize = () => {
      setCardsToShow(window.innerWidth >= 768 ? 3 : 1);
    };

    handleResize();

    let resizeTimer: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(handleResize, 100);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      window.removeEventListener('resize', debouncedResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  const maxIndex = Math.max(0, services.length - cardsToShow);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => Math.min(prevIndex + 1, maxIndex));
  }, [maxIndex]);

  const prevSlide = () => {
    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(Math.min(index, maxIndex));
    setIsAutoPlaying(false);
  };

  // Auto-play carousel every 6 seconds
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      if (currentIndex >= maxIndex) {
        setCurrentIndex(0);
      } else {
        nextSlide();
      }
    }, 6000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, nextSlide, currentIndex, maxIndex]);

  return (
    <section id="services" className="pt-12 pb-20 bg-neutral-50 dark:bg-neutral-dark-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-10 fade-in-up flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-neutral-900 dark:text-neutral-dark-900">
              How We Help Your Business Grow
            </h2>
          </div>
          <button
            onClick={() => setIsAutoPlaying(!isAutoPlaying)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-3 min-h-[44px] text-sm font-medium text-neutral-600 dark:text-neutral-dark-600 border border-neutral-300 dark:border-neutral-dark-400 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-dark-300 focus:outline-none focus:ring-2 focus:ring-primary-indigo transition-all"
            aria-label={isAutoPlaying ? 'Pause carousel auto-play' : 'Resume carousel auto-play'}
          >
            {isAutoPlaying ? (
              <>
                <svg className="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                Pause
              </>
            ) : (
              <>
                <svg className="w-4 h-4" aria-hidden="true" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                </svg>
                Play
              </>
            )}
          </button>
        </div>
        {/* Screen reader announcement for carousel position */}
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          Showing services {currentIndex + 1} to {Math.min(currentIndex + cardsToShow, services.length)} of {services.length}
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={() => {
              prevSlide();
              setIsAutoPlaying(false);
            }}
            disabled={currentIndex === 0}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 z-10 p-3 rounded-full bg-white dark:bg-neutral-dark-200 shadow-lg hover:shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-indigo disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            aria-label="Previous service"
          >
            <ChevronLeft className="w-6 h-6 text-primary-indigo dark:text-primary-rose" />
          </button>

          <button
            onClick={() => {
              nextSlide();
              setIsAutoPlaying(false);
            }}
            disabled={currentIndex >= maxIndex}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 z-10 p-3 rounded-full bg-white dark:bg-neutral-dark-200 shadow-lg hover:shadow-xl transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-indigo disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            aria-label="Next service"
          >
            <ChevronRight className="w-6 h-6 text-primary-indigo dark:text-primary-rose" />
          </button>

          {/* Carousel Track */}
          <div className="overflow-hidden">
            <div
              className="flex items-stretch"
              style={{
                transform: `translateX(-${currentIndex * (100 / cardsToShow)}%)`,
                transition: 'transform 520ms cubic-bezier(0.25, 1, 0.5, 1)',
              }}
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              {services.map((service, index) => (
                <div key={index} className="w-full md:w-1/3 flex-shrink-0 px-2 flex">
                  <ServiceCard
                    title={service.title}
                    result={service.result}
                    description={service.description}
                    outcomes={service.outcomes}
                    index={index}
                    learnMoreLink={service.link}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Carousel Indicators — visual dots inside 44px touch targets */}
          <div className="flex justify-center gap-1 mt-8">
            {Array.from({ length: maxIndex + 1 }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className="min-h-[44px] min-w-[44px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary-indigo rounded-full group"
                aria-label={`Go to slide ${index + 1}`}
              >
                <span
                  className={`block h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'w-8 bg-primary-indigo dark:bg-primary-rose'
                      : 'w-2 bg-neutral-300 dark:bg-neutral-dark-400 group-hover:bg-neutral-400 dark:group-hover:bg-neutral-dark-500'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-12 text-center fade-in-up animation-delay-300">
          <Link to="/services" className="btn-hover px-8 py-4 bg-primary-rose text-white rounded-lg font-semibold text-lg hover:bg-primary-rose/90 focus:outline-none focus:ring-2 focus:ring-primary-rose focus:ring-offset-2 transition-all inline-block">
            See All Our Services
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Services;
